import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { I18nService } from '@app/translations/i18n';
import { JwtHelperService } from '@auth0/angular-jwt';
import { AuthConfig, OAuthErrorEvent, OAuthService } from 'angular-oauth2-oidc';
import { JwksValidationHandler } from 'angular-oauth2-oidc-jwks';
import { Observable, of } from 'rxjs';
import {
  oauth2Config,
  oauth2Config_CL,
  oauth2Config_LOCAL,
  oauth2Config_SVTS,
} from '../../assets/configuration/oauth2-config';

const TAG = '[OAuth2]';

const BCC_ACR_VALUE = 'it:infocert:federated:bcc';

const HOSTNAME_ENV_MAP: Record<string, { label: string; config: AuthConfig }> =
  {
    'mysign.infocert.it': { label: 'PROD', config: oauth2Config },
    'mysign.eu-south-1.tsaws.infocert.it': {
      label: 'SVTS',
      config: oauth2Config_SVTS,
    },
    localhost: { label: 'LOCAL', config: oauth2Config_LOCAL },
    'mysigncl.infocert.it': { label: 'COLL', config: oauth2Config_CL },
    'mysign.clint.infocert.it': { label: 'COLL', config: oauth2Config_CL },
  };
const DEFAULT_ENV = { label: 'COLL', config: oauth2Config_CL };

function normalizeHostname(hostname: string): string {
  return hostname.replace(/^www\./i, '').toLowerCase();
}

function resolveOAuthEnvironment(): { label: string; config: AuthConfig } {
  const hostname = normalizeHostname(window.location.hostname);
  return HOSTNAME_ENV_MAP[hostname] ?? DEFAULT_ENV;
}

@Injectable({
  providedIn: 'root',
})
export class OAuth2Service {
  constructor(
    private oauthService: OAuthService,
    private jwtHelper: JwtHelperService,
    private i18nService: I18nService,
    private router: Router
  ) {
    this.setupEventLogging();
    this.configure();
  }

  private setupEventLogging() {
    this.oauthService.events.subscribe(event => {
      if (event instanceof OAuthErrorEvent) {
        console.error(
          `${TAG} Event: ${event.type}`,
          event.reason,
          event.params
        );
      } else {
        console.log(`${TAG} Event: ${event.type}`);
      }
    });
  }

  configure() {
    this.oauthService.setStorage(sessionStorage);

    const hostname = normalizeHostname(window.location.hostname);
    const match = resolveOAuthEnvironment();

    console.log(`${TAG} ENV = ${match.label}, hostname = ${hostname}`);

    const config: AuthConfig = {
      ...match.config,
      customQueryParams: {
        lang: this.i18nService.language.substring(0, 2),
        //acr_values: 'it:infocert:aal:3',
      },
    };

    console.log(`${TAG} Config:`, {
      clientId: config.clientId,
      issuer: config.issuer,
      redirectUri: config.redirectUri,
      scope: config.scope,
      responseType: config.responseType,
    });

    this.oauthService.configure(config);
    this.oauthService.tokenValidationHandler = new JwksValidationHandler();
    this.oauthService
      .loadDiscoveryDocument()
      .then(doc => {
        console.log(`${TAG} Discovery loaded:`, {
          tokenEndpoint: this.oauthService.tokenEndpoint,
          userinfoEndpoint: this.oauthService.userinfoEndpoint,
        });
        return this.oauthService.tryLogin();
      })
      .then(() => {
        const hasAccessToken = this.oauthService.hasValidAccessToken();
        const hasIdToken = this.oauthService.hasValidIdToken();
        console.log(`${TAG} tryLogin done:`, {
          hasAccessToken,
          hasIdToken,
          accessToken: hasAccessToken
            ? this.oauthService.getAccessToken()?.substring(0, 20) + '...'
            : null,
        });

        // Dopo il callback da ISAC4, la redirect URI è senza hash
        // (es. https://mysigncl.infocert.it/?code=xxx) quindi il router
        // Angular finisce sulla route protetta '' → AuthGuard → /login.
        // Se tryLogin() ha ottenuto i token, navighiamo a /oauth2 per
        // far partire checkSession() che crea la sessione backend.
        // In alcuni callback il solo access token è già disponibile e sufficiente
        // per avviare la sessione backend in /oauth2.
        if (hasAccessToken) {
          // Se arrivo qui, è un login valido: sblocco l'auto-login eventualmente
          // bloccato dopo un logout (flag usata per evitare rientro con semplice Ctrl+R).
          sessionStorage.removeItem('blockAutoLoginAfterLogout');

          console.log(`${TAG} Tokens received, navigating to /oauth2`, {
            hasAccessToken,
            hasIdToken,
          });
          this.router.navigate(['/oauth2']);
        }
      })
      .catch(error => {
        console.error(`${TAG} Discovery/tryLogin FAILED:`, error);
      });
  }

  login() {
    console.log(`${TAG} Initiating login flow...`);
    // Se l'utente sta facendo login esplicito, non deve essere bloccato dalla flag
    // anti auto-login (che serve solo post-logout).
    sessionStorage.removeItem('blockAutoLoginAfterLogout');
    this.oauthService.initLoginFlow();
  }

  loginWithBccFederation() {
    console.log(
      `${TAG} Initiating login flow with BCC federation (acr_values)...`
    );
    // Per BCC non dobbiamo essere bloccati dalla guardia post-logout.
    sessionStorage.removeItem('blockAutoLoginAfterLogout');
    // Usando initLoginFlow con custom parameters (tipizzato come any per
    // aggirare la firma ristretta delle typings presenti nel progetto).
    (this.oauthService as any).initLoginFlow(undefined, {
      acr_values: BCC_ACR_VALUE,
    });
  }

  logout() {
    console.log(`${TAG} Logout`);
    this.oauthService.logOut();
  }

  isLoggedIn(): Observable<boolean> {
    return of(this.oauthService.hasValidAccessToken());
  }

  hasToken(): Observable<string> {
    return of(this.oauthService.getAccessToken());
  }

  get accessToken() {
    return this.oauthService.getAccessToken();
  }

  get idToken() {
    return this.oauthService.getIdToken();
  }

  get hasValidTokens(): boolean {
    return (
      this.oauthService.hasValidAccessToken() && this.oauthService.hasValidIdToken()
    );
  }

  decodeToken(token: any) {
    return this.jwtHelper.decodeToken(token);
  }
}
